// What search engines read from a page: its title, description, canonical address and whether it may be listed. The static copies written by the server (manage.py export_blog_seo) carry these for crawlers
// that do not run JavaScript; this file keeps them right while the app is running, so every address has exactly one canonical form and the private or missing pages stay out of the results.

export const SITE_URL = "https://mraalionline.com";
const PRIVATE = [/^\/admin(\/|$)/, /^\/login(\/|$)/, /^\/co-work\/(dashboard|messages|assistant)(\/|$)/];

export const isPrivatePath = (pathname) => PRIVATE.some((re) => re.test(String(pathname || "")));

// The one address of a page: always https://mraalionline.com, no trailing slash (except the blog posts, which have one), no tracking parameters; ?tool=<tool> is part of an AI tool's address.
export function canonicalFor(pathname, search = "") {
  let path = `/${String(pathname || "").replace(/^\/+/, "")}`.replace(/\/{2,}/g, "/");
  if (path.startsWith("/blog/")) path = `${path.replace(/\/+$/, "")}/`;
  else if (path !== "/") path = path.replace(/\/+$/, "");
  const tool = new URLSearchParams(search).get("tool");
  const query = tool && /^[a-z0-9-]+$/.test(tool) && path !== "/" && !path.includes("/", 1) ? `?tool=${tool}` : "";
  return `${SITE_URL}${path}${query}`;
}

function upsert(selector, create, update) {
  let el = document.head.querySelector(selector);
  if (!el) { el = document.createElement(create.tag); Object.entries(create.attrs).forEach(([k, v]) => el.setAttribute(k, v)); document.head.appendChild(el); }
  update(el);
  return el;
}

export function setMeta(key, content, attr = "name") {
  upsert(`meta[${attr}="${key}"]`, { tag: "meta", attrs: { [attr]: key } }, (el) => el.setAttribute("content", content));
}

export function setCanonical(href) {
  upsert('link[rel="canonical"]', { tag: "link", attrs: { rel: "canonical" } }, (el) => el.setAttribute("href", href));
}

// index or noindex: "noindex, follow" keeps a page out of the results but lets its links be followed
export function setRobots(value) {
  setMeta("robots", value);
}

// The title of the page, also for social cards
export function setPageTitle(title) {
  document.title = title;
  setMeta("og:title", title, "property");
  setMeta("twitter:title", title);
}

// The text under the title in search results (about 150 characters), also for social cards
export function setPageDescription(text) {
  const clean = String(text || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;|&amp;/g, " ").replace(/\s+/g, " ").trim();
  if (!clean) return;
  const cut = clean.length > 158 ? `${clean.slice(0, 157).replace(/\s+\S*$/, "")}…` : clean;
  setMeta("description", cut);
  setMeta("og:description", cut, "property");
  setMeta("twitter:description", cut);
}

// A block of structured data (JSON-LD) that belongs to the page being shown; null removes it
export function setStructuredData(id, data) {
  const selector = `script[data-seo="${id}"]`;
  const existing = document.head.querySelector(selector);
  if (!data) { if (existing) existing.remove(); return; }
  const el = existing || Object.assign(document.createElement("script"), { type: "application/ld+json" });
  el.setAttribute("data-seo", id);
  el.textContent = JSON.stringify(data).replace(/</g, "\\u003c");
  if (!existing) document.head.appendChild(el);
}

export function breadcrumbData(crumbs) {
  return { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: crumbs.map(([name, path], i) => ({ "@type": "ListItem", position: i + 1, name, item: `${SITE_URL}${path}` })) };
}
